// frontend/src/internal/InternalDashboard.js
// 組織選択後のダッシュボード
// - 編集モード ON/OFF (作業理由必須)
// - 既存の管理者画面へジャンプ (閲覧 or 編集モード状態を引き継ぐ)
// - 監査ログ閲覧

import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useInternalStaff } from '../contexts/InternalStaffContext';

const InternalDashboard = () => {
  const navigate = useNavigate();
  const {
    internalStaff, selectedOrg, editSession, canEdit, loading,
    startEditMode, endEditMode, logout,
  } = useInternalStaff();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recentLogs, setRecentLogs] = useState([]);
  const [remainingTime, setRemainingTime] = useState('');

  // 編集セッション残り時間の表示更新
  useEffect(() => {
    if (!editSession) {
      setRemainingTime('');
      return;
    }
    const update = () => {
      const remainMs = new Date(editSession.expires_at).getTime() - Date.now();
      if (remainMs <= 0) {
        setRemainingTime('期限切れ');
        return;
      }
      const min = Math.floor(remainMs / 60000);
      const sec = Math.floor((remainMs % 60000) / 1000);
      setRemainingTime(`${min}:${String(sec).padStart(2,'0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [editSession]);

  // 直近の監査ログ取得
  useEffect(() => {
    if (!selectedOrg) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('internal_audit_logs')
          .select('id, action_type, action_target, reason, created_at, internal_staff_id')
          .eq('organization_id', selectedOrg.id)
          .order('created_at', { ascending: false })
          .limit(20);
        setRecentLogs(data || []);
      } catch (e) {
        console.warn('audit logs 取得失敗:', e.message);
      }
    })();
  }, [selectedOrg, editSession]);

  if (loading) return <div style={{ padding: 40 }}>読み込み中...</div>;
  if (!internalStaff) return <Navigate to="/internal/login" replace />;
  if (!selectedOrg) return <Navigate to="/internal/select-org" replace />;

  const handleStartEdit = async () => {
    if (!reason.trim()) {
      setError('作業理由は必須です (依頼番号 / 内容など)');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await startEditMode(reason);
      setShowReasonModal(false);
      setReason('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc, #eff6ff)',
      padding: '24px',
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* ヘッダー */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '24px',
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#0f172a' }}>Internal ダッシュボード</h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              {internalStaff.name} ({internalStaff.role})
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => {
              // 🔥 supabase_user/currentOrganization の偽装をクリアしてから選択画面へ
              localStorage.removeItem('supabase_user');
              localStorage.removeItem('currentOrganization');
              window.location.href = '/internal/select-org';
            }} style={{
              padding: '8px 14px', background: '#e0e7ff', color: '#3730a3',
              border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
            }}>
              組織変更
            </button>
            {internalStaff.role === 'admin' && (
              <a href="/internal/staff-management" style={{
                padding: '8px 14px', background: '#fef3c7', color: '#92400e',
                borderRadius: '8px', fontSize: '13px',
                textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center',
              }}>
                🔧 開発者管理
              </a>
            )}
            <button onClick={logout} style={{
              padding: '8px 14px', background: '#e5e7eb',
              border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
            }}>
              ログアウト
            </button>
          </div>
        </div>

        {/* 閲覧中組織カード */}
        <div style={{
          background: 'white', borderRadius: '14px', padding: '20px',
          marginBottom: '16px', border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: 4 }}>閲覧中</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
            {selectedOrg.name || '(名称未設定)'}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 2 }}>{selectedOrg.id}</div>
        </div>

        {/* 編集モード切替 */}
        <div style={{
          background: canEdit
            ? 'linear-gradient(135deg, #fee2e2, #fef3c7)'
            : 'white',
          borderRadius: '14px', padding: '20px',
          marginBottom: '16px',
          border: canEdit ? '2px solid #ef4444' : '1px solid #e5e7eb',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: 4,
                color: canEdit ? '#7f1d1d' : '#64748b' }}>
                {canEdit ? '⚠️ 編集モード ON' : '🔒 閲覧モード (編集不可)'}
              </div>
              {canEdit ? (
                <div style={{ fontSize: '12px', color: '#7f1d1d' }}>
                  理由: {editSession?.reason} / 残り {remainingTime}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {internalStaff.role === 'viewer'
                    ? 'viewer ロールは編集できません'
                    : '依頼を受けたら作業理由を入力して編集モードを ON にしてください'}
                </div>
              )}
            </div>
            <div>
              {canEdit ? (
                <button onClick={endEditMode} style={{
                  padding: '10px 20px',
                  background: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '8px',
                  fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                }}>
                  編集モードを OFF
                </button>
              ) : (
                internalStaff.role !== 'viewer' && (
                  <button onClick={() => setShowReasonModal(true)} style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                  }}>
                    編集モードを ON
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* 既存ページへのリンク */}
        <div style={{
          background: 'white', borderRadius: '14px', padding: '20px',
          marginBottom: '16px', border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
            管理画面へ移動
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {[
              ['/admin', '🗓️ シフト管理'],
              ['/surgery-request', '🏥 手術予定'],
              ['/admin-procedures', '📋 術式管理'],
              ['/settings', '⚙️ 設定'],
              ['/auto-assignment', '🤖 自動割当'],
              ['/daily-assignment', '👥 日次配置'],
            ].map(([path, label]) => (
              <button key={path} onClick={() => navigate(path)} style={{
                padding: '10px 14px', background: '#f1f5f9',
                color: '#0f172a', border: '1px solid #e5e7eb',
                borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
                textAlign: 'left',
              }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 監査ログ (直近) */}
        <div style={{
          background: 'white', borderRadius: '14px', padding: '20px',
          border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
            直近の操作ログ (この組織)
          </div>
          {recentLogs.length === 0 && (
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>まだログがありません</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recentLogs.map((log) => (
              <div key={log.id} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '12px', padding: '6px 10px',
                background: '#f8fafc', borderRadius: '6px',
              }}>
                <div>
                  <strong>{log.action_type}</strong>
                  {log.action_target && <> / {log.action_target}</>}
                  {log.reason && <> — {log.reason}</>}
                </div>
                <div style={{ color: '#94a3b8' }}>
                  {new Date(log.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 作業理由入力モーダル */}
      {showReasonModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '20px',
        }}>
          <div style={{
            background: 'white', borderRadius: '12px',
            padding: '24px', maxWidth: '480px', width: '100%',
          }}>
            <h2 style={{ margin: 0, marginBottom: 10, fontSize: '18px' }}>編集モードを ON にする</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 14px' }}>
              作業理由 (リモート依頼番号・作業内容など) を入力してください。
              編集モードは <strong>2 時間</strong>で自動的に OFF になります。
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="例: REQ-2026-0123 / シフトパターン設定の修正"
              rows={4}
              style={{
                width: '100%', padding: '10px 12px',
                borderRadius: '8px', border: '1px solid #d1d5db',
                fontSize: '13px', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            {error && (
              <div style={{
                background: '#fee2e2', color: '#7f1d1d',
                padding: '8px 12px', borderRadius: '8px',
                fontSize: '12px', marginTop: 10,
              }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowReasonModal(false); setReason(''); setError(''); }}
                style={{
                  padding: '8px 16px', background: '#e5e7eb',
                  border: 'none', borderRadius: '8px',
                  fontSize: '13px', cursor: 'pointer',
                }}>
                キャンセル
              </button>
              <button onClick={handleStartEdit} disabled={submitting}
                style={{
                  padding: '8px 16px',
                  background: submitting ? '#94a3b8' : '#dc2626',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontWeight: 700, fontSize: '13px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                {submitting ? '開始中...' : '編集モードを ON'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalDashboard;
